import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AddIconComponent, DeleteIconComponent } from '@kuiper/icons';
import { UserService } from '../user.service';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { AssignGroupUsersComponent } from '../../../../../common/components/assign-group-users/assign-group-users.component';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { RoleTypes } from '../../../../../common/constants/role-types.constant';
import { Option } from '../../../../../common/models/option.model';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../../common/constants/pagination.constants';
import { ActionList } from '../../../../../common/models/action-list.model';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-admins-list',
  imports: [
    BadgeComponent,
    GroupUserCardComponent,
    PaginatorComponent,
    TranslocoPipe,
    OverlayLoaderComponent,
    DeleteIconComponent,
    AddIconComponent,
    AssignGroupUsersComponent,
  ],
  templateUrl: './admins-list.component.html',
  styleUrl: './admins-list.component.scss',
  providers: [provideSystemTabsLayout()],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AdminsListComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  modalService = inject(ModalServiceService);
  transloco = inject(TranslocoService);
  userService = inject(UserService);
  toastService = inject(ToasterService);
  router = inject(Router);
  featureFlagsService = inject(FeatureFlagsService);
  tenantService = inject(TenantService);

  roleTypes = RoleTypes;
  removeUserModal = viewChild<TemplateRef<unknown>>('removeAdmin');
  assignAdminUsersDialog = viewChild<TemplateRef<unknown>>(
    'assignAdminUsersDialog'
  );

  adminUsersCards = signal<Option[]>([]);
  selectedUsers = signal<Option[]>([]);
  selectedUsersIds = signal<string[]>([]);
  currentPage = signal<number>(1);
  currentSize = signal<number>(15);
  totalPages = signal<number>(20);
  isLoading = signal<boolean>(false);
  totalAdminsCount = signal<string>('');
  selectedUserId = signal<string>('');
  isRemoveAdminLoading = signal<boolean>(false);
  isLoadingGroupUsers = signal<boolean>(false);
  isChangeAdminRoleLoading = signal<boolean>(false);
  modalCurrentPage = signal<number>(1);
  modalTotalPages = signal<number>(20);
  admins = signal<Option[]>([]);
  lastItemInPage = signal<boolean>(false);

  actionList: ActionList[] = [
    {
      key: 'view_details',
      hasIcon: true,
      label: this.transloco.translate('view_details_action_item'),
      hasDivider: false,
      labelCustomStyle: '',
      hasToggle: false,
      toggleValue: false,
    },
    {
      key: 'remove',
      hasIcon: true,
      label: this.transloco.translate('remove_action_item'),
      hasDivider: false,
      labelCustomStyle: 'text-destructive-500 font-medium text-xs',
      hasToggle: false,
      toggleValue: false,
    },
  ];

  ngOnInit() {
    this.getAdminsList();
  }

  handleJobTitleUsers(res: any) {
    this.admins.set(
      res.map((user: any) => {
        return {
          label: user.name,
          value: user.id.toString(),
          image: user.image,
          email: user.email,
        };
      })
    );
  }

  actionListItemClick(event: any) {
    if (event.action == 'remove') {
      this.selectedUserId.set(event.item.value);
      this.openModal();
    } else {
      this.router.navigateByUrl(
        this.tenantService.adminPath(
          `${ADMIN_ROUTES.USERS_DATABASE.DETAILS}/`
        ) + event.item.value
      );
    }
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getAdminsList();
  }

  getUsers(name = '', page = 1) {
    this.modalCurrentPage.set(page);
    this.isLoadingGroupUsers.set(true);
    this.userService
      .userLookup({
        name,
        page: this.modalCurrentPage(),
        filtersNotRole: this.roleTypes.ADMIN,
        filtersNotState: 'inactive',
        filtersNotStateCancelled: 'cancelled',
      })
      .subscribe(response => this.handleUsersResponse(response));
  }

  handleUsersResponse(response: any) {
    const mappedData = response.data.map((user: any) => ({
      ...user,
      isSelected: this.selectedUsers().some(
        selectedUser => selectedUser.value === user.value
      ),
    }));

    if (this.modalCurrentPage() === 1) {
      this.adminUsersCards.set(mappedData);
    } else {
      this.adminUsersCards().push(...mappedData);
    }
    this.modalTotalPages.set(response.meta.last_page);
    this.isLoadingGroupUsers.set(false);
  }

  openModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.removeUserModal(),
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  goBack() {
    history.back();
  }

  openAssignAdminUsersDialog() {
    this.clearSelectedUsers();
    this.currentPage.set(1);
    this.getUsers();

    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.assignAdminUsersDialog(),
      canNotCloseFromOutside: true,
      width: '50.5',
    });
  }

  clearSelectedUsers() {
    this.selectedUsers.set([]);
    this.selectedUsersIds.set([]);
  }

  onSelectAdminUser(user: Option) {
    user.isSelected = true;
    this.selectedUsers.update(users => [user, ...users]);

    // remove the selected item from the lookup list to avoid duplication
    this.adminUsersCards.set(
      this.adminUsersCards().filter(card => card.value !== user.value)
    );

    if (
      this.modalCurrentPage() < this.modalTotalPages() &&
      this.adminUsersCards().length < 7
    ) {
      this.getUsers('', this.modalCurrentPage() + 1);
    }
  }

  removeSelectedAdminUser(user: Option) {
    user.isSelected = false;
    this.selectedUsers.set(
      this.selectedUsers().filter(card => card.value !== user.value)
    );

    this.adminUsersCards.update(cards => [user, ...cards]);
  }

  updateSelectedUserIds() {
    this.selectedUsersIds().push(
      ...this.selectedUsers().map(user => user.value)
    );
  }

  changeAdminRole() {
    this.isChangeAdminRoleLoading.set(true);
    this.updateSelectedUserIds();

    this.userService
      .assignUserRole({
        role_name: this.roleTypes.ADMIN,
        user_ids: this.selectedUsersIds(),
      })
      .subscribe({
        next: () => {
          this.getAdminsList();
          this.clearSelectedUsers();
        },
        error: (error: any) => {
          this.toastService.add(
            error.message,
            '',
            'assets/images/icons/error.svg',
            'error'
          );
        },
        complete: () => {
          this.isChangeAdminRoleLoading.set(false);
          this.closeModal();
        },
      });
  }

  onRemoveAdmin() {
    this.isRemoveAdminLoading.set(true);
    this.userService
      .removeRoleFromUser(this.selectedUserId(), this.roleTypes.ADMIN)
      .subscribe({
        next: () => {
          this.handleRemoveAdminResponse();
        },
        error: (error: any) => {
          this.isRemoveAdminLoading.set(false);
          this.closeModal();
          this.toastService.add(
            error.error.message,
            '',
            'assets/images/icons/error.svg',
            'error'
          );
        },
      });
  }

  handleRemoveAdminResponse() {
    if (this.lastItemInPage() && this.currentPage() > 1) {
      this.currentPage.set(this.currentPage() - 1);
    }
    this.getAdminsList();
    this.closeModal();
    this.isRemoveAdminLoading.set(false);
  }

  getAdminsList() {
    this.isLoading.set(true);
    this.userService
      .getUserByRoleList(
        this.currentPage(),
        this.currentSize(),
        this.roleTypes.ADMIN
      )
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (res: any) => this.handleAdminsListResponse(res),
        complete: () => {
          this.handleJobTitleUsers(this.admins());
        },
      });
  }

  handleAdminsListResponse(response: any) {
    this.admins.set(response.data);
    this.totalAdminsCount.set(response.meta.total);
    this.totalPages.set(response.meta.last_page);
    this.lastItemInPage.set(response.meta.from - response.meta.to == 0);
  }
}
